import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { InvoiceAPI, UsersAPI } from "../lib/api";
import { formatCurrency, formatDate, STATUS_STYLES, STATUS_OPTIONS } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  MagnifyingGlass,
  Eye,
  CheckCircle,
  Clock,
  Warning,
  Tray,
  Trash,
  Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const receiptCount = (inv) =>
  (inv.line_items || []).reduce((n, li) => n + ((li.receipts && li.receipts.length) || 0), 0);

const StatCard = ({ label, value, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "bg-white",
    primary: "bg-primary/5 border-primary/40",
    warning: "bg-amber-50 border-amber-300",
    success: "bg-emerald-50 border-emerald-300",
  };
  return (
    <div
      className={`p-4 sm:p-5 border border-border relative ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="overline text-muted-foreground">{label}</div>
        <Icon size={18} weight="bold" className="opacity-60" />
      </div>
      <div className="mt-3 font-display text-2xl sm:text-3xl font-black tracking-tighter">
        {value}
      </div>
    </div>
  );
};

export default function Submissions() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("Sent"); // default to actionable
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([InvoiceAPI.list(), UsersAPI.list()])
      .then(([invs, users]) => {
        setAll(invs.filter((i) => i.is_subcontractor_invoice));
        setSubs(users.filter((u) => u.role === "subcontractor"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = all.filter((inv) => {
    const q = search.toLowerCase();
    const matchesQ =
      !q ||
      inv.invoice_number.toLowerCase().includes(q) ||
      (inv.owner_name || "").toLowerCase().includes(q) ||
      (inv.issuer_name || "").toLowerCase().includes(q);
    const matchesOwner = ownerFilter === "all" || inv.owner_id === ownerFilter;
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesQ && matchesOwner && matchesStatus;
  });

  const stats = {
    pending: all.filter((i) => i.status === "Sent").length,
    drafts: all.filter((i) => i.status === "Draft").length,
    paid: all.filter((i) => i.status === "Paid").length,
    overdue: all.filter((i) => i.status === "Overdue").length,
    pending_total: all
      .filter((i) => i.status === "Sent" || i.status === "Overdue")
      .reduce((s, i) => s + Number(i.total || 0), 0),
  };

  const handleMarkPaid = async (id) => {
    try {
      await InvoiceAPI.setStatus(id, "Paid");
      toast.success("Marked as Paid");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await InvoiceAPI.remove(deleteId);
      toast.success("Submission deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="submissions-page">
      <PageHeader
        overline="Admin"
        title="Submissions"
        description="Timesheet invoices submitted by your subcontractors. Review, mark as paid, or open the full invoice."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-6">
        <StatCard
          label="Awaiting You"
          value={loading ? "—" : stats.pending}
          icon={Tray}
          tone="primary"
          testid="stat-awaiting"
        />
        <StatCard
          label="Overdue"
          value={loading ? "—" : stats.overdue}
          icon={Warning}
          tone="warning"
          testid="stat-overdue"
        />
        <StatCard
          label="Drafts (Subs)"
          value={loading ? "—" : stats.drafts}
          icon={Clock}
          testid="stat-drafts"
        />
        <StatCard
          label="Unpaid Total"
          value={loading ? "—" : formatCurrency(stats.pending_total)}
          icon={Warning}
          tone="warning"
          testid="stat-unpaid-total"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder="Search by invoice # or sub name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm"
            data-testid="submission-search-input"
          />
        </div>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger
            className="w-full sm:w-56 rounded-sm"
            data-testid="submission-owner-filter"
          >
            <SelectValue placeholder="All subcontractors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subcontractors</SelectItem>
            {subs.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="w-full sm:w-44 rounded-sm"
            data-testid="submission-status-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="border border-border bg-white">
        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-muted/40 overline text-muted-foreground">
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-3">Submitted By</div>
          <div className="col-span-2">Issued</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Tray size={32} weight="bold" className="mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-muted-foreground mb-1">
              {all.length === 0
                ? "No submissions yet — your subcontractors haven't sent any invoices."
                : "Nothing matches these filters."}
            </div>
            {all.length === 0 && (
              <Link
                to="/subcontractors"
                className="text-sm text-primary hover:underline"
              >
                Create a subcontractor →
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((inv) => (
              <div
                key={inv.id}
                className="px-4 py-3 sm:px-6 sm:py-4 hover:bg-muted/40 transition-colors"
                data-testid={`submission-row-${inv.invoice_number}`}
              >
                {/* Mobile card */}
                <div className="lg:hidden flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold">
                        {inv.invoice_number}
                      </div>
                      <div className="text-sm truncate mt-0.5">
                        {inv.owner_name}
                      </div>
                      {inv.issuer_name && inv.issuer_name !== inv.owner_name && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          {inv.issuer_name}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm font-bold">
                        {formatCurrency(inv.total, inv.currency)}
                      </div>
                      <span
                        className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 border mt-1 ${
                          STATUS_STYLES[inv.status] || ""
                        }`}
                      >
                        {inv.status}
                      </span>
                      {receiptCount(inv) > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground" data-testid={`receipt-count-mobile-${inv.invoice_number}`}>
                          <Paperclip size={10} weight="bold" />
                          {receiptCount(inv)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <div>Due {formatDate(inv.due_date)}</div>
                    <div className="flex gap-1">
                      {inv.status !== "Paid" && (
                        <button
                          onClick={() => handleMarkPaid(inv.id)}
                          className="p-1.5 hover:bg-emerald-50 hover:text-emerald-700 rounded-sm"
                          data-testid={`mark-paid-mobile-${inv.invoice_number}`}
                          aria-label="Mark Paid"
                          title="Mark Paid"
                        >
                          <CheckCircle size={16} weight="bold" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="p-1.5 hover:bg-muted rounded-sm"
                        data-testid={`view-mobile-${inv.invoice_number}`}
                        aria-label="View"
                      >
                        <Eye size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => setDeleteId(inv.id)}
                        className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                        data-testid={`delete-mobile-${inv.invoice_number}`}
                        aria-label="Delete"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop grid */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-2 font-mono text-sm font-semibold">
                    {inv.invoice_number}
                  </div>
                  <div className="col-span-3 text-sm">
                    <div className="truncate font-semibold">{inv.owner_name}</div>
                    {inv.issuer_name && inv.issuer_name !== inv.owner_name && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {inv.issuer_name}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {formatDate(inv.issue_date)}
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {formatDate(inv.due_date)}
                  </div>
                  <div className="col-span-1">
                    <span
                      className={`inline-block text-xs font-semibold px-2 py-0.5 border ${
                        STATUS_STYLES[inv.status] || ""
                      }`}
                    >
                      {inv.status}
                    </span>
                  </div>
                  <div className="col-span-1 text-right font-mono text-sm font-semibold">
                    <div>{formatCurrency(inv.total, inv.currency)}</div>
                    {receiptCount(inv) > 0 && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground" data-testid={`receipt-count-${inv.invoice_number}`}>
                        <Paperclip size={10} weight="bold" />
                        {receiptCount(inv)}
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    {inv.status !== "Paid" && (
                      <button
                        onClick={() => handleMarkPaid(inv.id)}
                        className="p-1.5 hover:bg-emerald-50 hover:text-emerald-700 rounded-sm"
                        data-testid={`mark-paid-${inv.invoice_number}`}
                        aria-label="Mark Paid"
                        title="Mark as Paid"
                      >
                        <CheckCircle size={16} weight="bold" />
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                      className="p-1.5 hover:bg-muted rounded-sm"
                      aria-label="View"
                      title="Open"
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                    <button
                      onClick={() => setDeleteId(inv.id)}
                      className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                      aria-label="Delete"
                      title="Delete submission"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the invoice from your portal. The subcontractor will no longer see it either.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-submission-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
